import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Check if there's an error in the URL params
        const errorParam = searchParams.get('error');
        if (errorParam) {
          throw new Error(`Authentication failed: ${errorParam}`);
        }

        // Refresh user state after OAuth callback
        const user = await refreshUser();
        
        // Determine where to redirect the user
        const redirectTo = determineRedirectPath(user);
        
        // Small delay to ensure state is updated
        setTimeout(() => {
          navigate(redirectTo, { replace: true });
        }, 1000);
        
      } catch (error) {
        console.error('Error handling auth callback:', error);
        setError(error.message || 'Authentication failed');
        
        // Redirect to home after showing error
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
      }
    };

    handleCallback();
  }, [navigate, refreshUser, searchParams]);

  const determineRedirectPath = (user) => {
    // Check if user is admin
    if (user?.isAdmin || user?.role === 'admin') {
      return '/admin';
    }
    
    // For regular users, redirect to stories page (more engaging than home)
    return '/stories';
  };

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
        gap={2}
        px={2}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Redirecting to home page...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="50vh"
      gap={2}
    >
      <CircularProgress size={60} />
      <Typography variant="h6">
        Completing sign in...
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Please wait while we set up your account
      </Typography>
    </Box>
  );
};

export default AuthCallback;
